export type HelpLang = 'fr' | 'en';

const fr = {
  header: {
    title: "Guide d'utilisation",
    subtitle: "Tout ce que vous devez savoir pour utiliser Piktable",
  },
  overview: {
    title: "Vue d'ensemble",
    p1: "Piktable est votre outil de gestion des réservations. Depuis le dashboard, vous pouvez gérer les réservations du jour, consulter la liste des clients, visualiser le plan de salle et gérer la terrasse.",
    p2: "Utilisez la navigation par date (Hier / Aujourd'hui / Demain) pour parcourir les services, et les onglets pour basculer entre les vues.",
    dateNav: "navigation par date",
    tabs: "onglets",
    onboarding: {
      title: "Onboarding",
      desc: "Lors de la première connexion, un parcours guidé en 6 étapes vous aide à configurer votre établissement : zones, tables, créneaux horaires, mode de réservation, notifications et finalisation. Un widget de rappel sur le dashboard vous permet de reprendre là où vous en étiez.",
    },
    trialHelp: {
      title: "Aide durant l'essai",
      desc: "Pendant votre période d'essai, une bannière vous propose de planifier un appel de configuration gratuit avec notre équipe pour vous aider à démarrer.",
    },
  },
  reservations: {
    title: "Réservations",
    viewModes: {
      title: "Modes de vue",
      intro: "Trois modes sont disponibles :",
      grouped: "Groupé",
      groupedDesc: "réservations regroupées par créneau horaire",
      list: "Liste",
      listDesc: "toutes les réservations en liste chronologique",
      timeline: "Timeline",
      timelineDesc: "barre temporelle visuelle par table",
    },
    actions: {
      title: "Actions sur une réservation",
      arrived: "Arrivé",
      arrivedDesc: "marque le client comme présent",
      cancel: "Annuler",
      cancelDesc: "annule la réservation (email optionnel)",
      noShow: "No-show",
      noShowDesc: "disponible si le client est en retard de 15+ minutes",
      modify: "Modifier",
      modifyDesc: "changez la date, l'heure, le nombre de personnes, la table, les notes",
      quickTable: "Changement rapide de table",
      quickTableDesc: "cliquez sur le nom de la table pour la changer sans ouvrir le dialogue complet",
      emails: "Emails",
      emailsDesc: "envoyez manuellement une confirmation, modification ou annulation",
      notes: "Notes internes",
      notesDesc: "visibles uniquement par l'équipe",
    },
    walkIn: {
      title: "Walk-in",
      desc: "Créez une réservation instantanée pour un client sans réservation. Accessible depuis le bouton « Walk-in » sur le dashboard ou le plan de salle. Sélectionnez le nombre de personnes, la table et validez — le client est immédiatement marqué comme arrivé.",
    },
    waitlist: {
      title: "Liste d'attente",
      desc: "Quand toutes les tables sont occupées, ajoutez des clients à la liste d'attente. Une bannière affiche les clients en attente avec un minuteur. Dès qu'une table se libère, vous pouvez assigner un client depuis la waitlist en un clic.",
    },
    selfService: {
      title: "Modification / annulation par le client",
      desc: "Les emails de confirmation contiennent des liens permettant au client de modifier ou annuler sa réservation lui-même. Informez vos clients que ces options sont disponibles dans leur email.",
    },
    children: {
      title: "Enfants & poussettes",
      desc: "Lors de la création d'une réservation ou d'un walk-in, vous pouvez indiquer le nombre d'enfants et la présence d'une poussette. Le système filtre automatiquement les tables compatibles. Le mode de comptage enfants (compte entier, demi-place ou ignoré) est configurable dans les paramètres.",
    },
    terracePreference: {
      title: "Préférence terrasse",
      desc: "Lors de la recherche de disponibilité, choisissez entre « Terrasse », « Intérieur » ou « Sans préférence ». Le système filtre les tables en conséquence et respecte les éventuelles fermetures de terrasse.",
    },
    summary: {
      title: "Résumé du jour",
      desc: "La barre de résumé affiche : total réservations, total personnes, annulations (dont no-shows) et taux d'arrivée.",
    },
    tools: {
      title: "Outils",
      csvExport: "Export CSV",
      csvExportDesc: "téléchargez les réservations du jour",
      print: "Impression",
      printDesc: "mise en page optimisée A4",
      search: "Recherche",
      searchDesc: "trouvez une réservation par nom, email ou téléphone sur toutes les dates",
    },
  },
  clients: {
    title: "Clients",
    intro: "L'onglet Clients affiche la liste de tous les clients avec leur historique de réservations.",
    loyalty: {
      title: "Niveaux de fidélité",
      new: "Nouveau",
      newCount: "1 visite",
      regular: "Habitué",
      regularCount: "2-4 visites",
      loyal: "Fidèle",
      loyalCount: "5-9 visites",
      vip: "VIP",
      vipCount: "10+ visites",
    },
    notes: {
      title: "Notes client",
      desc: "Ajoutez des notes persistantes (préférences, allergies, habitudes). Elles s'affichent automatiquement lors des prochaines réservations de ce client.",
    },
    edit: {
      title: "Modifier un client",
      desc: "Vous pouvez modifier le nom, l'email, le téléphone et les notes. ⚠️ Changer l'email affecte toutes les futures communications.",
    },
    allergies: {
      title: "Allergies & tags",
      desc: "Ajoutez des tags d'allergies (gluten, lactose, noix, etc.) et des tags personnalisés sur chaque fiche client. Les allergies sont affichées avec une alerte visuelle lors de la création d'une réservation pour ce client.",
    },
    statusBlacklist: {
      title: "Statut, blacklist & no-show",
      desc: "Chaque client peut avoir un statut (VIP, etc.) visible sur sa fiche. Marquez un client comme blacklisté pour bloquer ses réservations en ligne. Les no-shows passés sont également indiqués sur la fiche client.",
    },
    consent: {
      title: "Consentement marketing (nLPD)",
      desc: "Le consentement email et SMS est enregistré avec horodatage pour respecter la nLPD. Vous pouvez consulter et modifier ces préférences depuis la fiche client.",
    },
    importExport: {
      title: "Import / Export",
      desc: "Importez des clients depuis un fichier CSV (compatible Aleno, TheFork, Excel). Le système détecte automatiquement le format et les doublons par email. Exportez la liste complète des clients en CSV depuis le bouton d'export.",
    },
  },
  floorPlan: {
    title: "Plan de salle",
    consultation: {
      title: "Mode consultation",
      desc: "Chaque table affiche son nom, sa capacité et son statut. Survolez pour voir les détails de la réservation assignée. Les zones sont accessibles via des onglets.",
    },
    dragDrop: {
      title: "Drag & drop",
      desc: "Glissez une réservation depuis le panneau latéral vers une table pour l'y assigner. Si la table est déjà occupée, un swap est proposé.",
    },
    editMode: {
      title: "Mode édition",
      desc: "Activez le mode édition (✏️) pour déplacer les tables, changer leur taille (S/M/L) ou leur forme (rond/carré), et les assigner à des zones. N'oubliez pas de sauvegarder.",
    },
    zoom: {
      title: "Zoom & navigation",
      desc: "Utilisez les boutons +/- pour zoomer. Cliquez sur le fond et glissez pour vous déplacer. La barre de recherche permet de localiser une table.",
    },
  },
  terrace: {
    title: "Terrasse",
    intro: "Cet onglet apparaît uniquement si la terrasse est activée dans les paramètres.",
    weather: {
      title: "Météo",
      desc: "Prévisions sur 16 jours avec indicateur de mauvais temps et suggestion de fermeture.",
    },
    closeOpen: {
      title: "Fermer / Ouvrir la terrasse",
      desc: "Fermez la terrasse pour une journée entière ou un créneau spécifique. Les réservations impactées sont listées avec possibilité de réassignation et d'email aux clients.",
    },
  },
  availability: {
    title: "Disponibilités",
    intro: "La page Disponibilités permet de rechercher des tables disponibles par date, créneau et nombre de personnes.",
    categories: {
      title: "Catégories de résultats",
      recommended: "Recommandée",
      recommendedDesc: "table optimale (capacité la plus proche)",
      available: "Disponible",
      availableDesc: "tables libres compatibles",
      manual: "Manuelle",
      manualDesc: "tables non-réservables en ligne mais physiquement libres (bypass staff)",
    },
    bookCta: "Cliquez sur « Réserver » pour ouvrir le formulaire pré-rempli.",
  },
  tables: {
    title: "Gestion des tables",
    simple: {
      title: "Tables simples",
      intro: "Ajoutez, modifiez ou supprimez des tables. Propriétés configurables :",
      capacity: "Capacité",
      capacityDesc: "(min / max) — contrôle l'assignation automatique",
      bookable: "Réservable",
      bookableDesc: "si désactivé, visible staff uniquement",
      terraceFlag: "Terrasse",
      terraceFlagDesc: "identifie comme table extérieure",
      children: "Enfants / Poussette",
      childrenDesc: "exclut de l'auto-assignation si désactivé",
      priority: "Priorité",
      priorityDesc: "ordre d'assignation (plus bas = proposé en premier)",
    },
    combined: {
      title: "Tables combinées",
      desc: "Regroupez plusieurs tables physiques pour les grands groupes. La capacité totale est calculée automatiquement. L'auto-combinaison génère les combinaisons possibles.",
    },
    blocking: {
      title: "Périodes de blocage",
      desc: "Bloquez temporairement une table (travaux, événement privé). Pendant le blocage, la table disparaît des résultats de disponibilité.",
    },
  },
  timeSlots: {
    title: "Créneaux horaires",
    intro: "Gérez les services de votre établissement (midi, soir, brunch, etc.).",
    config: {
      title: "Configuration",
      item1: "Nom, heure de début et de fin",
      item2: "Jours d'ouverture par créneau",
    },
    closures: {
      title: "Fermetures",
      service: "Fermeture de service",
      serviceDesc: "ferme un créneau spécifique pour une période",
      establishment: "Fermeture d'établissement",
      establishmentDesc: "ferme tous les créneaux (jours fériés, vacances)",
    },
  },
  toolsFeatures: {
    title: "Outils & fonctionnalités",
    ai: {
      title: "Assistant IA",
      desc: "Posez des questions sur vos réservations, la disponibilité ou les clients. Accessible depuis le menu profil → « Chat IA ». Quota de crédits mensuel selon votre plan.",
    },
    pwa: {
      title: "Installation mobile (PWA)",
      desc: "Installez Piktable comme une application sur votre téléphone. Menu profil → « Installer l'app ». L'application se lance en plein écran depuis l'écran d'accueil.",
    },
    spam: {
      title: "Alertes spam",
      desc: "Si un nombre anormal de réservations est détecté en peu de temps, une bannière d'alerte apparaît avec les détails.",
    },
    pause: {
      title: "Pause des réservations",
      desc: "Quand activée par l'admin, un bandeau informe que les réservations en ligne sont suspendues. Le staff peut toujours créer des réservations manuellement.",
    },
    whatsNew: {
      title: "Quoi de neuf",
      desc: "Consultez les dernières mises à jour et nouvelles fonctionnalités depuis le bouton en haut du dashboard.",
    },
    compactView: {
      title: "Vue compacte des tables",
      desc: "Toggle en haut du dashboard pour afficher un résumé visuel du statut de chaque table avec un code couleur.",
    },
    autoEmails: {
      title: "Emails automatiques",
      desc: "Deux types d'emails automatiques sont configurables : les rappels (envoyés la veille de la réservation) et les suivis (envoyés le lendemain avec un lien vers votre page d'avis Google, TripAdvisor, etc.). Activez-les dans Paramètres → Emails.",
    },
    reviewLinks: {
      title: "Liens d'avis",
      desc: "Configurez vos liens Google Business, TripAdvisor, TheFork, etc. dans les paramètres d'établissement. Un assistant vous aide à trouver votre lien Google. Ces liens sont utilisés dans les emails de suivi automatique.",
    },
  },
  restrictions: {
    title: "Fonctionnalités réservées Admin",
    intro: "Les fonctionnalités suivantes ne sont accessibles qu'au rôle Admin :",
    items: [
      "Facturation et abonnement",
      "Statistiques globales",
      "Gestion des utilisateurs",
      "Paramètres d'établissement (nom, email, adresse)",
      "Configuration des emails et notifications",
      "Mode de réservation (SLOT / TIME)",
      "Durée par défaut et limites de personnes",
      "Widget de réservation (code embed)",
    ],
    contact: "Contactez votre administrateur si vous avez besoin d'accéder à ces fonctionnalités.",
  },
  shortcuts: {
    title: "Raccourcis utiles",
    items: [
      { label: "Jour suivant", value: "Bouton « Demain »" },
      { label: "Revenir à aujourd'hui", value: "Bouton « Aujourd'hui »" },
      { label: "Rafraîchir les données", value: "Bouton 🔄" },
      { label: "Chercher une réservation", value: "Onglet « Recherche »" },
      { label: "Réservation rapide", value: "Disponibilités → Réserver" },
      { label: "Trouver une table", value: "Recherche plan de salle" },
    ],
  },
};

const en: typeof fr = {
  header: {
    title: "User Guide",
    subtitle: "Everything you need to know to use Piktable",
  },
  overview: {
    title: "Overview",
    p1: "Piktable is your reservation management tool. From the dashboard, you can manage today's reservations, browse the client list, view the floor plan and manage the terrace.",
    p2: "Use the date navigation (Yesterday / Today / Tomorrow) to browse services, and tabs to switch between views.",
    dateNav: "date navigation",
    tabs: "tabs",
    onboarding: {
      title: "Onboarding",
      desc: "On first login, a guided 6-step flow helps you set up your establishment: zones, tables, time slots, booking mode, notifications and completion. A reminder widget on the dashboard lets you pick up where you left off.",
    },
    trialHelp: {
      title: "Trial help",
      desc: "During your trial period, a banner offers to schedule a free setup call with our team to help you get started.",
    },
  },
  reservations: {
    title: "Reservations",
    viewModes: {
      title: "View modes",
      intro: "Three modes are available:",
      grouped: "Grouped",
      groupedDesc: "reservations grouped by time slot",
      list: "List",
      listDesc: "all reservations in chronological order",
      timeline: "Timeline",
      timelineDesc: "visual time bar per table",
    },
    actions: {
      title: "Reservation actions",
      arrived: "Arrived",
      arrivedDesc: "marks the guest as present",
      cancel: "Cancel",
      cancelDesc: "cancels the reservation (optional email)",
      noShow: "No-show",
      noShowDesc: "available if the guest is 15+ minutes late",
      modify: "Edit",
      modifyDesc: "change date, time, party size, table, notes",
      quickTable: "Quick table change",
      quickTableDesc: "click the table name to reassign without opening the full dialog",
      emails: "Emails",
      emailsDesc: "manually send a confirmation, modification or cancellation email",
      notes: "Internal notes",
      notesDesc: "visible to staff only",
    },
    walkIn: {
      title: "Walk-in",
      desc: 'Create an instant reservation for a guest without a booking. Accessible from the "Walk-in" button on the dashboard or floor plan. Select party size, choose a table and confirm — the guest is immediately marked as arrived.',
    },
    waitlist: {
      title: "Waitlist",
      desc: "When all tables are occupied, add guests to the waitlist. A banner displays waiting guests with a timer. As soon as a table frees up, you can assign a waitlisted guest in one click.",
    },
    selfService: {
      title: "Guest self-service (modify / cancel)",
      desc: "Confirmation emails include links allowing the guest to modify or cancel their reservation themselves. Let your guests know these options are available in their email.",
    },
    children: {
      title: "Children & strollers",
      desc: "When creating a reservation or walk-in, you can specify the number of children and whether a stroller is needed. The system automatically filters compatible tables. The children counting mode (full, half or ignored) is configurable in settings.",
    },
    terracePreference: {
      title: "Terrace preference",
      desc: 'When searching for availability, choose between "Terrace", "Indoor" or "No preference". The system filters tables accordingly and respects any terrace closures.',
    },
    summary: {
      title: "Daily summary",
      desc: "The summary bar shows: total reservations, total guests, cancellations (incl. no-shows) and arrival rate.",
    },
    tools: {
      title: "Tools",
      csvExport: "CSV Export",
      csvExportDesc: "download today's reservations",
      print: "Print",
      printDesc: "A4-optimised layout",
      search: "Search",
      searchDesc: "find a reservation by name, email or phone across all dates",
    },
  },
  clients: {
    title: "Clients",
    intro: "The Clients tab displays all clients with their reservation history.",
    loyalty: {
      title: "Loyalty levels",
      new: "New",
      newCount: "1 visit",
      regular: "Regular",
      regularCount: "2-4 visits",
      loyal: "Loyal",
      loyalCount: "5-9 visits",
      vip: "VIP",
      vipCount: "10+ visits",
    },
    notes: {
      title: "Client notes",
      desc: "Add persistent notes (preferences, allergies, habits). They appear automatically on this client's future reservations.",
    },
    edit: {
      title: "Edit a client",
      desc: "You can edit name, email, phone and notes. ⚠️ Changing the email affects all future communications.",
    },
    allergies: {
      title: "Allergies & tags",
      desc: "Add allergy tags (gluten, lactose, nuts, etc.) and custom tags to each client profile. Allergies are displayed with a visual alert when creating a reservation for that client.",
    },
    statusBlacklist: {
      title: "Status, blacklist & no-show",
      desc: "Each client can have a status (VIP, etc.) visible on their profile. Mark a client as blacklisted to block their online reservations. Past no-shows are also indicated on the client profile.",
    },
    consent: {
      title: "Marketing consent (nLPD)",
      desc: "Email and SMS consent is recorded with timestamps to comply with nLPD. You can view and edit these preferences from the client profile.",
    },
    importExport: {
      title: "Import / Export",
      desc: "Import clients from a CSV file (compatible with Aleno, TheFork, Excel). The system automatically detects the format and duplicates by email. Export the full client list as CSV from the export button.",
    },
  },
  floorPlan: {
    title: "Floor plan",
    consultation: {
      title: "View mode",
      desc: "Each table displays its name, capacity and status. Hover to see assigned reservation details. Zones are accessible via tabs.",
    },
    dragDrop: {
      title: "Drag & drop",
      desc: "Drag a reservation from the side panel onto a table to assign it. If the table is already occupied, a swap is offered.",
    },
    editMode: {
      title: "Edit mode",
      desc: "Enable edit mode (✏️) to move tables, change their size (S/M/L) or shape (round/square), and assign them to zones. Don't forget to save.",
    },
    zoom: {
      title: "Zoom & navigation",
      desc: "Use the +/- buttons to zoom. Click and drag the background to pan. The search bar helps locate a table.",
    },
  },
  terrace: {
    title: "Terrace",
    intro: "This tab only appears if the terrace is enabled in settings.",
    weather: {
      title: "Weather",
      desc: "16-day forecast with bad weather indicator and closure suggestion.",
    },
    closeOpen: {
      title: "Close / Open the terrace",
      desc: "Close the terrace for a full day or a specific slot. Impacted reservations are listed with options to reassign and email guests.",
    },
  },
  availability: {
    title: "Availability",
    intro: "The Availability page lets you search for available tables by date, time slot and party size.",
    categories: {
      title: "Result categories",
      recommended: "Recommended",
      recommendedDesc: "optimal table (closest capacity match)",
      available: "Available",
      availableDesc: "compatible free tables",
      manual: "Manual",
      manualDesc: "not bookable online but physically free (staff bypass)",
    },
    bookCta: 'Click "Book" to open the pre-filled form.',
  },
  tables: {
    title: "Table management",
    simple: {
      title: "Single tables",
      intro: "Add, edit or delete tables. Configurable properties:",
      capacity: "Capacity",
      capacityDesc: "(min / max) — controls automatic assignment",
      bookable: "Bookable",
      bookableDesc: "if disabled, visible to staff only",
      terraceFlag: "Terrace",
      terraceFlagDesc: "marks as outdoor table",
      children: "Children / Stroller",
      childrenDesc: "excluded from auto-assignment if disabled",
      priority: "Priority",
      priorityDesc: "assignment order (lower = suggested first)",
    },
    combined: {
      title: "Combined tables",
      desc: "Group multiple physical tables for large parties. Total capacity is calculated automatically. Auto-combine generates possible combinations.",
    },
    blocking: {
      title: "Blocking periods",
      desc: "Temporarily block a table (maintenance, private event). While blocked, the table disappears from availability results.",
    },
  },
  timeSlots: {
    title: "Time slots",
    intro: "Manage your establishment's services (lunch, dinner, brunch, etc.).",
    config: {
      title: "Configuration",
      item1: "Name, start and end time",
      item2: "Opening days per slot",
    },
    closures: {
      title: "Closures",
      service: "Service closure",
      serviceDesc: "closes a specific slot for a period",
      establishment: "Establishment closure",
      establishmentDesc: "closes all slots (holidays, vacations)",
    },
  },
  toolsFeatures: {
    title: "Tools & features",
    ai: {
      title: "AI Assistant",
      desc: 'Ask questions about your reservations, availability or clients. Accessible from the profile menu → "AI Chat". Monthly credit quota based on your plan.',
    },
    pwa: {
      title: "Mobile install (PWA)",
      desc: 'Install Piktable as an app on your phone. Profile menu → "Install app". The app launches fullscreen from the home screen.',
    },
    spam: {
      title: "Spam alerts",
      desc: "If an abnormal number of reservations is detected in a short time, an alert banner appears with details.",
    },
    pause: {
      title: "Pause reservations",
      desc: "When enabled by the admin, a banner informs that online reservations are suspended. Staff can still create reservations manually.",
    },
    whatsNew: {
      title: "What's new",
      desc: "Check the latest updates and new features from the button at the top of the dashboard.",
    },
    compactView: {
      title: "Compact table view",
      desc: "Toggle at the top of the dashboard to display a visual summary of each table's status with colour coding.",
    },
    autoEmails: {
      title: "Automatic emails",
      desc: "Two types of automatic emails are configurable: reminders (sent the day before the reservation) and follow-ups (sent the day after with a link to your Google, TripAdvisor review page, etc.). Enable them in Settings → Emails.",
    },
    reviewLinks: {
      title: "Review links",
      desc: "Configure your Google Business, TripAdvisor, TheFork links, etc. in the establishment settings. A helper guides you to find your Google link. These links are used in automatic follow-up emails.",
    },
  },
  restrictions: {
    title: "Admin-only features",
    intro: "The following features are only accessible to the Admin role:",
    items: [
      "Billing and subscription",
      "Global statistics",
      "User management",
      "Establishment settings (name, email, address)",
      "Email and notification configuration",
      "Booking mode (SLOT / TIME)",
      "Default duration and party size limits",
      "Booking widget (embed code)",
    ],
    contact: "Contact your administrator if you need access to these features.",
  },
  shortcuts: {
    title: "Useful shortcuts",
    items: [
      { label: "Next day", value: '"Tomorrow" button' },
      { label: "Back to today", value: '"Today" button' },
      { label: "Refresh data", value: "🔄 button" },
      { label: "Find a reservation", value: '"Search" tab' },
      { label: "Quick booking", value: "Availability → Book" },
      { label: "Find a table", value: "Floor plan search" },
    ],
  },
};

export const helpTranslations = { fr, en } as const;
